import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import DOMPurify from 'dompurify'
import {
    MdAdd,
    MdArrowBack,
    MdDelete,
    MdSend,
    MdSupportAgent,
} from 'react-icons/md'
import RichTextEditor from '@/components/rich-text-editor'
import ConfirmModal from '@/components/confirm-modal'
import {
    createTicket,
    deleteTicket,
    getAllTickets,
    getMyTickets,
    getTicket,
    replyTicket,
    updateTicket,
    uploadHelpImage,
    type HelpCenterCapabilities,
    type HelpTicket,
    type HelpTicketPriority,
    type HelpTicketStatus,
} from '@/lib/api/help-center/help-center-api'

async function handleEditorImageUpload(file: File): Promise<string> {
    const res = await uploadHelpImage(file)
    if (res.success && res.result?.url) return res.result.url
    toast.error(res.error || 'Não foi possível enviar a imagem')
    throw new Error(res.error || 'upload-failed')
}

const STATUS_META: Record<HelpTicketStatus, { label: string; badge: string }> = {
    OPEN: { label: 'Aberto', badge: 'badge-info' },
    IN_PROGRESS: { label: 'Em andamento', badge: 'badge-warning' },
    ANSWERED: { label: 'Respondido', badge: 'badge-secondary' },
    RESOLVED: { label: 'Resolvido', badge: 'badge-success' },
    CLOSED: { label: 'Fechado', badge: 'badge-ghost' },
}

const PRIORITY_META: Record<HelpTicketPriority, { label: string; badge: string }> = {
    LOW: { label: 'Baixa', badge: 'badge-ghost' },
    NORMAL: { label: 'Normal', badge: 'badge-ghost' },
    HIGH: { label: 'Alta', badge: 'badge-warning' },
    URGENT: { label: 'Urgente', badge: 'badge-error' },
}

const STATUS_OPTIONS: HelpTicketStatus[] = [
    'OPEN',
    'IN_PROGRESS',
    'ANSWERED',
    'RESOLVED',
    'CLOSED',
]

const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleString('pt-BR') : ''

interface Props {
    capabilities: HelpCenterCapabilities
    scope: 'mine' | 'all'
}

type View = 'list' | 'new' | 'detail'

export default function TicketsTab({ capabilities, scope }: Props) {
    const [searchParams, setSearchParams] = useSearchParams()
    const [view, setView] = useState<View>('list')

    const [tickets, setTickets] = useState<HelpTicket[]>([])
    const [loadingList, setLoadingList] = useState(true)
    const [statusFilter, setStatusFilter] = useState<HelpTicketStatus | ''>('')
    const [search, setSearch] = useState('')

    const [selected, setSelected] = useState<HelpTicket | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)

    // Novo ticket
    const [newTitle, setNewTitle] = useState('')
    const [newPriority, setNewPriority] = useState<HelpTicketPriority>('NORMAL')
    const [newContent, setNewContent] = useState('')
    const [creating, setCreating] = useState(false)

    // Resposta
    const [replyContent, setReplyContent] = useState('')
    const [replying, setReplying] = useState(false)

    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const loadList = useCallback(async () => {
        setLoadingList(true)
        try {
            if (scope === 'all' && capabilities.canReadAllTickets) {
                const res = await getAllTickets({
                    status: statusFilter || undefined,
                    search: search.trim() || undefined,
                })
                if (res.success && res.result) setTickets(res.result.items)
            } else {
                const res = await getMyTickets()
                if (res.success && res.result) setTickets(res.result)
            }
        } finally {
            setLoadingList(false)
        }
    }, [scope, statusFilter, search, capabilities.canReadAllTickets])

    const openDetail = useCallback(async (id: number) => {
        setView('detail')
        setLoadingDetail(true)
        try {
            const res = await getTicket(id)
            if (res.success && res.result) {
                setSelected(res.result)
            } else {
                toast.error(res.error || 'Não foi possível abrir o ticket')
                setView('list')
            }
        } finally {
            setLoadingDetail(false)
        }
    }, [])

    useEffect(() => {
        if (view === 'list') loadList()
    }, [view, loadList])

    // Ao alternar entre "Meus" e "Todos" (tabs do topo), volta para a listagem
    useEffect(() => {
        setView('list')
        setSelected(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope])

    // Deep link ?ticket=ID
    useEffect(() => {
        const ticketId = searchParams.get('ticket')
        if (ticketId) {
            openDetail(Number(ticketId))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleCreate = async () => {
        if (!newTitle.trim()) {
            toast.warning('Informe um título')
            return
        }
        if (!newContent.trim() || newContent.trim() === '<p></p>') {
            toast.warning('Descreva o seu problema ou dúvida')
            return
        }
        setCreating(true)
        try {
            const res = await createTicket({
                title: newTitle.trim(),
                content: newContent,
                priority: newPriority,
            })
            if (res.success && res.result) {
                toast.success('Ticket criado com sucesso')
                setNewTitle('')
                setNewContent('')
                setNewPriority('NORMAL')
                setSelected(res.result)
                setView('detail')
            } else {
                toast.error(res.error || 'Não foi possível criar o ticket')
            }
        } finally {
            setCreating(false)
        }
    }

    const handleReply = async () => {
        if (!selected) return
        if (!replyContent.trim() || replyContent.trim() === '<p></p>') {
            toast.warning('Escreva uma mensagem')
            return
        }
        setReplying(true)
        try {
            const res = await replyTicket(selected.id, replyContent)
            if (res.success && res.result) {
                setSelected(res.result)
                setReplyContent('')
            } else {
                toast.error(res.error || 'Não foi possível enviar a resposta')
            }
        } finally {
            setReplying(false)
        }
    }

    const handleChangeStatus = async (status: HelpTicketStatus) => {
        if (!selected) return
        const res = await updateTicket(selected.id, { status })
        if (res.success && res.result) {
            setSelected(res.result)
            toast.success('Status atualizado')
        } else {
            toast.error(res.error || 'Não foi possível atualizar o status')
        }
    }

    const handleDelete = async () => {
        if (!selected) return
        setDeleting(true)
        try {
            const res = await deleteTicket(selected.id)
            if (res.success) {
                toast.success('Ticket excluído')
                setConfirmDelete(false)
                backToList()
            } else {
                toast.error(res.error || 'Não foi possível excluir')
            }
        } finally {
            setDeleting(false)
        }
    }

    const backToList = () => {
        setSelected(null)
        setView('list')
        const next = new URLSearchParams(searchParams)
        next.delete('ticket')
        setSearchParams(next, { replace: true })
    }

    // ===== NOVO TICKET =====
    if (view === 'new') {
        return (
            <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={() => setView('list')}
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <h2 className="text-lg font-semibold">Novo ticket</h2>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Título *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                                placeholder="Resuma o assunto em poucas palavras"
                                maxLength={200}
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Prioridade</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={newPriority}
                                onChange={e =>
                                    setNewPriority(e.target.value as HelpTicketPriority)
                                }
                            >
                                {(Object.keys(PRIORITY_META) as HelpTicketPriority[]).map(
                                    p => (
                                        <option key={p} value={p}>
                                            {PRIORITY_META[p].label}
                                        </option>
                                    ),
                                )}
                            </select>
                        </div>
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">Descrição *</span>
                        </label>
                        <RichTextEditor
                            content={newContent}
                            onChange={setNewContent}
                            placeholder="Descreva com o máximo de detalhes: o que aconteceu, passos para reproduzir, prints, etc."
                            minHeight="280px"
                            onImageUpload={handleEditorImageUpload}
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3">
                        <button className="btn btn-ghost" onClick={() => setView('list')}>
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={handleCreate}
                            disabled={creating}
                        >
                            {creating && (
                                <span className="loading loading-spinner loading-sm" />
                            )}
                            Enviar ticket
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // ===== DETALHE =====
    if (view === 'detail') {
        return (
            <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between gap-2">
                    <button className="btn btn-ghost btn-sm gap-2" onClick={backToList}>
                        <MdArrowBack className="text-xl" /> Voltar
                    </button>
                    {capabilities.canManageTickets && selected && (
                        <div className="flex items-center gap-2">
                            <select
                                className="select select-bordered select-sm"
                                value={selected.status}
                                onChange={e =>
                                    handleChangeStatus(e.target.value as HelpTicketStatus)
                                }
                            >
                                {STATUS_OPTIONS.map(s => (
                                    <option key={s} value={s}>
                                        {STATUS_META[s].label}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="btn btn-ghost btn-sm text-error"
                                onClick={() => setConfirmDelete(true)}
                            >
                                <MdDelete className="text-lg" />
                            </button>
                        </div>
                    )}
                </div>

                {loadingDetail || !selected ? (
                    <div className="flex justify-center py-20">
                        <span className="loading loading-spinner loading-lg text-secondary" />
                    </div>
                ) : (
                    <>
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`badge ${STATUS_META[selected.status].badge}`}>
                                    {STATUS_META[selected.status].label}
                                </span>
                                <span
                                    className={`badge ${PRIORITY_META[selected.priority].badge}`}
                                >
                                    Prioridade: {PRIORITY_META[selected.priority].label}
                                </span>
                                <span className="text-xs text-base-content/50">
                                    #{selected.id} · {formatDate(selected.createdAt)}
                                </span>
                            </div>
                            <h2 className="text-xl font-bold text-base-content">
                                {selected.title}
                            </h2>
                            <p className="text-sm text-base-content/60">
                                Aberto por {selected.authorName}
                            </p>
                        </div>

                        {/* Thread */}
                        <div className="flex flex-col gap-4">
                            {selected.messages?.map(msg => (
                                <div
                                    key={msg.id}
                                    className={`rounded-xl border p-4 shadow-sm ${
                                        msg.isAgent
                                            ? 'bg-secondary/5 border-secondary/30'
                                            : 'bg-base-100 border-base-300/50'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-2 text-sm">
                                        {msg.isAgent && (
                                            <MdSupportAgent className="text-secondary text-lg" />
                                        )}
                                        <span className="font-semibold text-base-content">
                                            {msg.authorName}
                                        </span>
                                        {msg.isAgent && (
                                            <span className="badge badge-secondary badge-sm">
                                                Suporte
                                            </span>
                                        )}
                                        <span className="text-xs text-base-content/50 ml-auto">
                                            {formatDate(msg.createdAt)}
                                        </span>
                                    </div>
                                    <div
                                        className="prose prose-sm max-w-none [&_p:empty]:min-h-[1em]"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(msg.content),
                                        }}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Resposta */}
                        {selected.status !== 'CLOSED' ? (
                            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                                <span className="label-text font-medium">Responder</span>
                                <RichTextEditor
                                    content={replyContent}
                                    onChange={setReplyContent}
                                    placeholder="Escreva sua mensagem..."
                                    minHeight="160px"
                                    onImageUpload={handleEditorImageUpload}
                                />
                                <div className="flex justify-end">
                                    <button
                                        className="btn btn-secondary gap-2"
                                        onClick={handleReply}
                                        disabled={replying}
                                    >
                                        {replying ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : (
                                            <MdSend />
                                        )}
                                        Enviar resposta
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="alert bg-base-200 text-base-content/70 text-sm">
                                Este ticket está fechado.
                            </div>
                        )}
                    </>
                )}

                <ConfirmModal
                    isOpen={confirmDelete}
                    onClose={() => setConfirmDelete(false)}
                    onConfirm={handleDelete}
                    title="Excluir ticket"
                    message="Tem certeza de que deseja excluir este ticket? Esta ação não pode ser desfeita."
                    confirmButtonText="Excluir"
                    cancelButtonText="Cancelar"
                    loading={deleting}
                />
            </div>
        )
    }

    // ===== LISTA =====
    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <h2 className="text-base font-semibold text-base-content">
                    {scope === 'all' ? 'Todos os tickets' : 'Meus tickets'}
                </h2>
                <button
                    className="btn btn-secondary btn-sm gap-2"
                    onClick={() => setView('new')}
                >
                    <MdAdd className="text-lg" /> Novo ticket
                </button>
            </div>

            {scope === 'all' && capabilities.canReadAllTickets && (
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        className="input input-bordered input-sm flex-1 min-w-[200px]"
                        placeholder="Buscar por título ou autor..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && loadList()}
                    />
                    <select
                        className="select select-bordered select-sm"
                        value={statusFilter}
                        onChange={e =>
                            setStatusFilter(e.target.value as HelpTicketStatus | '')
                        }
                    >
                        <option value="">Todos os status</option>
                        {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>
                                {STATUS_META[s].label}
                            </option>
                        ))}
                    </select>
                    <button className="btn btn-sm" onClick={loadList}>
                        Filtrar
                    </button>
                </div>
            )}

            {loadingList ? (
                <div className="flex justify-center py-20">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : tickets.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-dashed border-base-300 p-12 text-center text-base-content/60">
                    <MdSupportAgent className="text-4xl mx-auto mb-3 opacity-40" />
                    <p>Nenhum ticket por aqui ainda.</p>
                    <button
                        className="btn btn-secondary btn-sm gap-2 mt-4"
                        onClick={() => setView('new')}
                    >
                        <MdAdd /> Abrir meu primeiro ticket
                    </button>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {tickets.map(ticket => (
                        <button
                            key={ticket.id}
                            className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 text-left hover:border-secondary/50 transition-colors flex items-center gap-4"
                            onClick={() => openDetail(ticket.id)}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`badge badge-sm ${
                                            STATUS_META[ticket.status].badge
                                        }`}
                                    >
                                        {STATUS_META[ticket.status].label}
                                    </span>
                                    <span className="font-medium text-base-content truncate">
                                        {ticket.title}
                                    </span>
                                </div>
                                <p className="text-xs text-base-content/50 mt-1">
                                    #{ticket.id}
                                    {scope === 'all' && ` · ${ticket.authorName}`} ·{' '}
                                    {formatDate(ticket.lastReplyAt || ticket.createdAt)}
                                </p>
                            </div>
                            {typeof ticket._count?.messages === 'number' && (
                                <span className="badge badge-ghost">
                                    {ticket._count.messages} msg
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
